import { Outlet, useNavigate, NavLink } from 'react-router-dom'
import {
  FolderOpen,
  Users,
  ArrowRight,
  LogOut,
  User,
  ChevronsUpDown,
  Shield,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils/cn'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { supabase } from '@/lib/supabase/client'
import { TopBar } from '@/components/shared/top-bar'

interface AdminNavItem {
  key: string
  label: string
  icon: React.ElementType
  path: string
}

const NAV_ITEMS: AdminNavItem[] = [
  { key: 'projects', label: 'پروژه‌ها', icon: FolderOpen, path: '/admin/projects' },
  { key: 'users', label: 'کاربران', icon: Users, path: '/admin/users' },
]

function AdminSidebar() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const signOut = useAuthStore((s) => s.signOut)

  const userProps = user
    ? {
        name: user.name ?? user.email,
        email: user.email,
        initials: (user.name ?? user.email)
          .split(' ')
          .map((w: string) => w[0])
          .join('')
          .slice(0, 2)
          .toUpperCase(),
      }
    : null

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    signOut()
    navigate('/sign-in')
  }

  return (
    <aside className="h-full w-56 bg-card border-e border-border flex flex-col py-3 shrink-0">
      {/* Header */}
      <div className="px-4 pb-3 mb-1 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-primary/20 flex items-center justify-center">
            <Shield className="w-3.5 h-3.5 text-primary" />
          </div>
          <span className="text-xs font-semibold text-foreground leading-tight">
            پنل مدیریت پلتفرم
          </span>
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-2 space-y-0.5 mt-1">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon
          return (
            <NavLink
              key={item.key}
              to={item.path}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors group',
                  isActive
                    ? 'bg-primary/15 text-foreground border-s-2 border-primary ps-[9px] [&>svg]:text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted border-s-2 border-transparent ps-[9px]'
                )
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="flex-1 truncate font-medium">{item.label}</span>
            </NavLink>
          )
        })}

        {/* Back to projects */}
        <button
          onClick={() => navigate('/projects')}
          className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors text-muted-foreground hover:text-foreground hover:bg-muted border-s-2 border-transparent ps-[9px] mt-2"
        >
          <ArrowRight className="w-4 h-4 shrink-0" />
          <span className="flex-1 truncate font-medium text-start">بازگشت به پروژه‌ها</span>
        </button>
      </nav>

      {/* User dropdown */}
      {userProps && (
        <div className="px-2 pt-2 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 hover:bg-muted transition-colors text-start">
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarFallback className="bg-primary/20 text-primary text-xs font-medium">
                    {userProps.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate leading-none mb-0.5">
                    {userProps.name}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate leading-none">
                    {userProps.email}
                  </p>
                </div>
                <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-52">
              <DropdownMenuItem onClick={() => navigate('/account')}>
                <User className="w-4 h-4 me-2" />
                حساب کاربری
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleSignOut}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="w-4 h-4 me-2" />
                خروج
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </aside>
  )
}

export function AdminShell() {
  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <TopBar showProject={false} />
      <div className="flex flex-1 overflow-hidden">
        <AdminSidebar />
        <main className="flex-1 overflow-y-auto bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
